import cx from 'classnames'

import { builderLightText } from '@/blocks/utils'
import { Button } from '@/components/Button'
import { Media, type MediaProps } from '@/components/Media'
import { HeadingS, SubHeading, type TypographyColorModifier, BodyM } from '@/components/Typography'
import { decodeURL } from '@/core/utils/dom/decodeURL'
import { useIsMobile } from '@/hooks/useIsMobile'
import type { BlockButtonProps, TitleProps } from '@/models'

import { MediaCategoryItemStyles } from './styles'

export interface MediaCategoryItemProps extends BlockButtonProps {
  title: TitleProps
  subtitle: TitleProps
  content: string
  position: 'left' | 'right'
  midia: MediaProps & { imageSize?: 'sm' | 'md' | 'lg' | 'xg' }
  className?: string
  button?: BlockButtonProps['button'] & { rounded?: boolean }
  hasButton?: boolean
  colorModifier?: TypographyColorModifier
}

export const MediaCategoryItem = ({
  title,
  subtitle,
  content,
  position,
  midia: medusaMidiaProps,
  hasButton,
  button,
  className,
  colorModifier,
}: MediaCategoryItemProps) => {
  const lightText = builderLightText(colorModifier)

  const buttonProps = {
    modifier: button?.type || 'outline',
    label: button?.label,
    action: button?.action?.type,
    value: decodeURL(button?.action?.value),
    rounded: button?.rounded,
  }

  const imageSize = medusaMidiaProps.imageSize || 'sm'
  delete medusaMidiaProps.imageSize

  const isMobile = useIsMobile()

  const mediaSize = () => {
    const desktopSizes = {
      sm: { width: 352, height: 264 },
      md: { width: 504, height: 282 },
      lg: { width: 405, height: 534 },
      xg: { width: 549, height: 723 },
    }
    const mobileSizes = {
      sm: { width: 352, height: 264 },
      md: { width: 336, height: 188 },
      lg: { width: 270, height: 356 },
      xg: { width: 366, height: 482 },
    }

    const sizes = isMobile ? mobileSizes : desktopSizes
    return sizes[imageSize]
  }

  return (
    <MediaCategoryItemStyles className={cx('mdci--bg', className)}>
      <div
        className={cx('container', `container--${position}`, {
          'container--has_button': hasButton,
        })}
      >
        <div className="media">
          <Media
            {...mediaSize()}
            className="media__content"
            layout="intrinsic"
            {...medusaMidiaProps}
          />
        </div>

        {(title || subtitle || content) && (
          <div className="content">
            {title && title.text && (
              <HeadingS light={lightText} dark="high" tag={title.tag || 'h3'} className="title">
                {title.text}
              </HeadingS>
            )}

            {subtitle && subtitle.text && (
              <SubHeading
                light={lightText}
                dark="medium"
                tag={subtitle.tag || 'h4'}
                className="title"
              >
                {subtitle.text}
              </SubHeading>
            )}

            {content && <BodyM light={lightText} dark="medium" html={content} tag="div" />}
          </div>
        )}

        {hasButton && buttonProps && (
          <div className="cta_container">
            <Button size="lg" className="button" {...buttonProps}>
              {buttonProps.label}
            </Button>
          </div>
        )}
      </div>
    </MediaCategoryItemStyles>
  )
}
